import {skipToken} from '@reduxjs/toolkit/query';
import {Button, DataStatus, Separator} from 'platform/components';
import {
  Box,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Show,
  Stack,
  Text,
  ThemeColorTextPath,
  ThemeIconKey,
  VStack,
} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {filter, head, isNil, isNotNil} from 'ramda';

import {
  useCreateAutotracerPlReportMutation,
  useGetAutotracerPlPreviewQuery,
  useGetVehicleAutotracerPlReportsQuery,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';
import {CebiaWidgetAlert, CebiaWidgetLogo} from '@dms/teas';

import {composePath, openInNewTab, suffixTestId, TestIdProps, useNavigate} from 'shared/web';

const POLLING_INTERVAL = 10000;

export interface VehicleHistoryReportWidgetVehicle {
  id: string;
  vin?: string | null;
  make?: string | null;
  modelFamily?: string | null;
}

export interface VehicleHistoryReportWidgetProps extends TestIdProps {
  vehicleId: string;
  vehicle?: VehicleHistoryReportWidgetVehicle | null;
}

function ReportStatusLine(props: {
  label: string;
  value: string;
  valueColor?: ThemeColorTextPath;
  icon: ThemeIconKey;
}) {
  return (
    <HStack spacing={2} align="center" wrap="nowrap">
      <Icon size={6} value={props.icon} />
      <VStack spacing={1}>
        <Text size="xSmall" color="secondary">
          {props.label}
        </Text>
        <Text size="small" color={props.valueColor ?? 'secondary'}>
          {props.value}
        </Text>
      </VStack>
    </HStack>
  );
}

export function VehicleHistoryReportWidget(props: VehicleHistoryReportWidgetProps) {
  const navigate = useNavigate();
  const [pendingOpenReportId, setPendingOpenReportId] = useState<string | null>(null);

  const vehicleId = props.vehicleId;
  const vehicle = props.vehicle;

  const isVehicleValid = Boolean(vehicle?.vin && vehicle?.make && vehicle?.modelFamily);

  const [previewPollingInterval, setPreviewPollingInterval] = useState<number | undefined>();
  const [reportsPollingInterval, setReportsPollingInterval] = useState<number | undefined>();

  const {
    data: preview,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useGetAutotracerPlPreviewQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken, {
    pollingInterval: previewPollingInterval,
  });

  const {
    data: reports,
    isLoading: isReportsLoading,
    isError: isReportsError,
  } = useGetVehicleAutotracerPlReportsQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken, {
    pollingInterval: reportsPollingInterval,
  });

  const [createReport, {isLoading: isCreating}] = useCreateAutotracerPlReportMutation();

  const latestReport = head(reports ?? []);
  const isLatestReportPending =
    latestReport?.status === 'accepted' || latestReport?.status === 'pending';
  const latestCompletedReport = head(
    filter((report) => report.status === 'completed', reports ?? [])
  );
  const canViewLatestReport =
    isNotNil(latestCompletedReport?.url) || isNotNil(latestCompletedReport?.pdfUrl);

  useEffect(() => {
    setPreviewPollingInterval(preview?.status === 'pending' ? POLLING_INTERVAL : undefined);
  }, [preview?.status]);

  useEffect(() => {
    setReportsPollingInterval(isLatestReportPending ? POLLING_INTERVAL : undefined);
  }, [isLatestReportPending]);

  useEffect(() => {
    if (
      isNil(pendingOpenReportId) ||
      isNil(latestReport) ||
      latestReport.id !== pendingOpenReportId
    ) {
      return;
    }
    const url = latestReport.url ?? latestReport.pdfUrl;
    if (isNotNil(url)) {
      openInNewTab(url);
      setPendingOpenReportId(null);
    }
  }, [latestReport, pendingOpenReportId]);

  const handleGetReport = () => {
    createReport({vehicleId})
      .unwrap()
      .then((response) => {
        setPendingOpenReportId(response.id);
      })
      .catch(handleApiError);
  };

  const handleViewReport = () => {
    const url = latestCompletedReport?.url ?? latestCompletedReport?.pdfUrl;
    if (isNotNil(url)) {
      openInNewTab(url);
    }
  };

  return (
    <VStack spacing={2}>
      <Box backgroundColor="palettes.neutral.10.100" borderRadius="medium" overflow="hidden">
        <Stack direction={['column', 'row']}>
          <CebiaWidgetLogo alt={i18n.t('page.integrations.labels.autotracerPlTitle')} />
          <Box padding={4} width="100%" backgroundColor="palettes.neutral.10.100">
            <VStack spacing={3}>
              <HStack justify="space-between" width="100%">
                <Heading size={4}>{i18n.t('page.integrations.labels.autotracerPlTitle')}</Heading>
                {/* <Button TODO T20-115202 Define what should this link do
                  variant="link"
                  size="small"
                  title={i18n.t('page.integrations.labels.autotracerPlLearnMore')}
                  onClick={() => openInNewTab('#')}
                /> */}
              </HStack>
              <Text size="xSmall" color="secondary">
                {i18n.t('page.integrations.labels.autotracerPlDescription')}
              </Text>
              <DataStatus
                isLoading={isPreviewLoading || isReportsLoading}
                isError={isPreviewError || isReportsError}
              >
                <VStack spacing={2} width="100%">
                  <Separator spacing={2} />
                  <Grid columns={[1, 2]} spacing={2}>
                    <GridItem>
                      <ReportStatusLine
                        icon="specification/mileage"
                        label={i18n.t('page.integrations.labels.autotracerPlMileage')}
                        value={
                          preview?.isMileageAvailable
                            ? i18n.t('page.integrations.labels.autotracerPlAvailable')
                            : i18n.t('page.integrations.labels.autotracerPlNotAvailable')
                        }
                        valueColor={preview?.isMileageAvailable ? 'success' : 'secondary'}
                      />
                    </GridItem>
                    <GridItem>
                      <ReportStatusLine
                        icon="automotive/damage"
                        label={i18n.t('page.integrations.labels.autotracerPlDamage')}
                        value={
                          preview?.isDamagesAvailable
                            ? i18n.t('page.integrations.labels.autotracerPlRecordFound')
                            : i18n.t('page.integrations.labels.autotracerPlNoRecord')
                        }
                        valueColor={preview?.isDamagesAvailable ? 'warning' : 'secondary'}
                      />
                    </GridItem>
                  </Grid>
                  <Separator spacing={2} />
                </VStack>
              </DataStatus>
              <HStack spacing={2}>
                <Show when={isNotNil(latestCompletedReport)}>
                  <Button
                    data-testid={suffixTestId('vehicleHistoryReportWidget-viewReport', props)}
                    variant="secondary"
                    rightIcon="action/launch"
                    isDisabled={!canViewLatestReport}
                    onClick={handleViewReport}
                    title={i18n.t('page.integrations.labels.cebiaAutotracerViewReport')}
                  />
                </Show>
                <Button
                  data-testid={suffixTestId('vehicleHistoryReportWidget-getReport', props)}
                  variant="secondary"
                  isDisabled={!isVehicleValid}
                  isLoading={isCreating || isLatestReportPending}
                  onClick={handleGetReport}
                  title={i18n.t('page.integrations.labels.cebiaAutotraceGetReport')}
                />
              </HStack>
            </VStack>
          </Box>
        </Stack>
      </Box>
      <Show when={!isVehicleValid}>
        <CebiaWidgetAlert
          colorScheme="warning"
          message={i18n.t('page.integrations.labels.cebiaAutotraceRequiredFields')}
          actionText={i18n.t('entity.vehicle.labels.editVehicle')}
          actionCallback={
            vehicle?.id
              ? () => navigate(composePath(vehiclesRoutes.editV2, {params: {id: vehicle.id}}))
              : undefined
          }
        />
      </Show>
    </VStack>
  );
}
